import { useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface SwipeAction {
  id: string;
  label: string;
  icon: IconName;
  tone?: 'danger';
  onClick: () => void;
}

const ACTION_WIDTH = 72;
const START_THRESHOLD = 12;

/** Row that reveals action buttons when dragged to the left (touch/pen/mouse). */
export function SwipeRow({
  actions,
  className,
  children,
}: {
  actions: SwipeAction[];
  className: string;
  children: ReactNode;
}) {
  const full = ACTION_WIDTH * actions.length;
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ x: number; y: number; base: number; id: number } | null>(null);
  const movedRef = useRef(false);
  const offsetRef = useRef(0);

  const move = (value: number) => {
    offsetRef.current = value;
    setOffset(value);
  };

  return (
    <div
      className={`${className} direct-chat-swipe`}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startRef.current = {
          x: event.clientX,
          y: event.clientY,
          base: offsetRef.current,
          id: event.pointerId,
        };
        movedRef.current = false;
      }}
      onPointerMove={(event) => {
        const start = startRef.current;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (!movedRef.current) {
          if (Math.abs(dy) > START_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
            startRef.current = null;
            return;
          }
          if (Math.abs(dx) < START_THRESHOLD) return;
          movedRef.current = true;
          setDragging(true);
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }
        move(Math.max(-full, Math.min(0, start.base + dx)));
      }}
      onPointerUp={(event) => {
        if (startRef.current?.id !== event.pointerId) return;
        startRef.current = null;
        if (!movedRef.current) return;
        setDragging(false);
        move(offsetRef.current < -full / 2 ? -full : 0);
      }}
      onPointerCancel={() => {
        startRef.current = null;
        setDragging(false);
        move(0);
      }}
      // A swipe must not also be treated as a tap on the row.
      onClickCapture={(event) => {
        if (movedRef.current) {
          event.stopPropagation();
          event.preventDefault();
          movedRef.current = false;
        } else if (offsetRef.current !== 0) {
          event.stopPropagation();
          event.preventDefault();
          move(0);
        }
      }}
    >
      <div className="direct-chat-swipe-actions" aria-hidden={offset === 0}>
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            tabIndex={offset === 0 ? -1 : 0}
            className={`direct-chat-swipe-action${action.tone ? ` is-${action.tone}` : ''}`}
            style={{ width: ACTION_WIDTH }}
            onClick={() => {
              move(0);
              action.onClick();
            }}
          >
            <Icon name={action.icon} size={18} />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
      <div
        className={`direct-chat-swipe-content${dragging ? ' is-dragging' : ''}`}
        style={{ transform: offset ? `translateX(${offset}px)` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}
